import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IProjectAsset, AssetType } from '@packages/global';
import AssetForm from './AssetForm';

interface SubjectAssetEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  initialData?: IProjectAsset;
  defaultAssetType?: AssetType;
  projectId?: string;
  episodeId?: string;
  onSave: (data: any) => Promise<void>;
}

export default function SubjectAssetEditDrawer({
  isOpen,
  onClose,
  title,
  initialData,
  defaultAssetType = AssetType.CHARACTER,
  projectId,
  episodeId,
  onSave
}: SubjectAssetEditDrawerProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>(
    initialData?.assetType || defaultAssetType
  );

  // Update selected asset type when props change
  React.useEffect(() => {
    if (initialData) {
      setSelectedAssetType(initialData.assetType);
    } else {
      setSelectedAssetType(defaultAssetType);
    }
  }, [defaultAssetType, initialData]);

  const handleSubmit = async (data: any) => {
    const submitData = {
      ...data,
      assetType: selectedAssetType,
      projectId: projectId || null,
      episodeId: episodeId || null
    };
    await onSave(submitData);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-[600px] bg-bg-surface shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-subtle">
            <h2 className="text-xl font-semibold text-gray-50">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Asset Form */}
              <AssetForm
                assetType={selectedAssetType}
                editingAsset={initialData || null}
                onSubmit={handleSubmit}
                onCancel={onClose}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}